import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ErrorModel } from '@/shared/api/BaseAPI.model'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import DevicesAPI from '../api/DevicesAPI'
import { GetDeviceResponse } from '../api/DevicesAPI.model'

export const DEVICES_QUERY_KEY = 'devices'

export function useDevicesQuery() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()

  const { data: devicesList = [], isLoading: devicesLoading } = useQuery<
    GetDeviceResponse[]
  >({
    queryKey: [DEVICES_QUERY_KEY],
    queryFn: () => DevicesAPI.getDevices()
  })

  const invalidateDevices = () => {
    queryClient.invalidateQueries({ queryKey: [DEVICES_QUERY_KEY] })
  }

  // The backend deletes one device per call (DELETE /devices/:deviceId), so a
  // multi-row selection fans out into one request per id.
  const { mutate: deleteDevices, isPending: isDeletingDevices } = useMutation<
    void[],
    ErrorModel,
    string[]
  >({
    mutationFn: (deviceIds: string[]) =>
      Promise.all(deviceIds.map(deviceId => DevicesAPI.deleteDevice(deviceId))),
    onSuccess: () => {
      showSnackbar(
        t('ALERTS.DELETE_DEVICES_SUCCESS.TITLE'),
        t('ALERTS.DELETE_DEVICES_SUCCESS.DESCRIPTION'),
        'success'
      )
      invalidateDevices()
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.DELETE_DEVICES_ERROR.TITLE'),
        t('ALERTS.DELETE_DEVICES_ERROR.DESCRIPTION'),
        'error'
      )
      // A partial failure leaves the list stale, refetch to show what survived
      invalidateDevices()
    }
  })

  return {
    devicesList,
    devicesLoading,
    invalidateDevices,
    deleteDevices,
    isDeletingDevices
  }
}
